import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { AppContext } from '../../../../providers/AppProvider';
import {
  PostItemFragment,
  QueueContext,
  QueueFilterInput,
  QueueSortOrder,
  QueueSortType,
  useGetPostListAscendingLazyQuery,
  useGetPostListDescendingLazyQuery,
  useGetPostQueueCountLazyQuery,
  useGetTimeslotsQuery
} from '../../../../models/graphql';
import { PostScheduleViewProps } from '../PostSchedule.view';
import { PostScheduleLibraryController } from './PostScheduleLibrary.controller';
import { showToast } from '../../../../helpers/showToast';
import { clearPostCache, defaultQueueInput } from '../PostSchedule.apollo';
import { QueueCountContext } from '../../../../providers/QueueCountProvider';
import { PostScheduleContext } from '../../../../providers/PostScheduleProvider';
import { isGetPostScheduleListQuery, LimitsReachedType } from '../PostScheduleList/PostScheduleList.apollo';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { PostScheduleHelpers } from '../postScheduleHelpers';
import { LibrarySort } from '../../../../constants/post';

export type PostScheduleLibraryApolloProps = PostScheduleViewProps & {
  // ...
};

export const PostScheduleLibraryApollo = (props: PostScheduleLibraryApolloProps): JSX.Element => {
  const { queueFilters } = props;
  const numberOfPostsToFetch = 25;

  const { selectedWorkspaceId } = useContext(AppContext);
  const {
    loadingInitial,
    setLoadingInitial,
    triggerPostReFetch,
    setTriggerPostReFetch,
    triggerDeleteReFetch,
    setTriggerDeleteReFetch
  } = useContext(PostScheduleContext);

  const { dispatch } = useContext(TopNavContext);
  const { queueCounts } = useContext(QueueCountContext);

  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [limitsReached, setLimitsReached] = useState<LimitsReachedType>({ upper: false, lower: false });
  const [corePostItems, setCorePostItems] = useState<PostItemFragment[]>([]);

  const [sortOrder, setSortOrder] = useState<QueueSortOrder>(QueueSortOrder.DESCENDING);
  const [sortType, setSortType] = useState<QueueSortType>(QueueSortType.ORIGINAL_POST_CREATED_TIME);

  const timeslotsResults = useGetTimeslotsQuery({
    variables: { workspaceId: selectedWorkspaceId as string }
  });

  const [getPostScheduleListAscending, postListAscendingResults] = useGetPostListAscendingLazyQuery({
    fetchPolicy: 'network-only'
  });

  // Descending is only used for scroll up || upper bounds queries
  const [getPostScheduleListDescending, postListDescendingResults] = useGetPostListDescendingLazyQuery({
    fetchPolicy: 'network-only'
  });

  const [getPostQueueCount, postQueueCountResults] = useGetPostQueueCountLazyQuery({
    fetchPolicy: 'network-only'
  });

  const { setQueueCounts } = useContext(QueueCountContext);

  const { fetchMore: postAscendingResultsFetchMore, variables: postListAscendingResultsVariables } =
    postListAscendingResults;
  const { fetchMore: postDescendingResultsFetchMore, variables: postListDescendingResultsVariables } =
    postListDescendingResults;

  const queueFiltersInput = useMemo(
    (): QueueFilterInput => ({
      status: queueFilters.status,
      searchTerm: queueFilters.searchTerm,
      socialProfileId: queueFilters.socialProfileId,
      categoryId: queueFilters.categoryId,
      timing: queueFilters.timing,
      evergreen: queueFilters.evergreen,
      source: queueFilters.source,
      content: queueFilters.content
    }),
    [queueFilters]
  );

  const handleLimitCalculation = useCallback((scrollPosition: 'TOP' | 'BOTTOM', isLimit: boolean) => {
    if (scrollPosition === 'BOTTOM') {
      setLimitsReached(old => ({
        upper: old.upper,
        lower: isLimit
      }));
    } else {
      setLimitsReached(old => ({
        lower: old.lower,
        upper: isLimit
      }));
    }
  }, []);

  const getPostLibraryQueueCounts = useCallback(
    (filters: QueueFilterInput) => {
      getPostQueueCount({
        variables: {
          workspaceId: selectedWorkspaceId as string,
          queueFilters: filters,
          queueContext: QueueContext.LIBRARY
        }
      })
        .then(results => {
          if (results.data?.workspace.queueCounts) {
            setQueueCounts(results.data.workspace.queueCounts);
          }
        })
        .catch((error: ApolloError) => {
          showToast({
            severity: 'error',
            summary: 'Retrieving Queue Counts',
            detail: remapApolloErrors(error)
          });
        });
    },
    [getPostQueueCount, selectedWorkspaceId, setQueueCounts]
  );

  const getPostScheduleLibraryItems = useCallback(
    (options: object, scrollPosition?: 'TOP' | 'BOTTOM') => {
      setLoadingInitial(true);

      const queryFn =
        sortOrder === QueueSortOrder.DESCENDING ? getPostScheduleListDescending : getPostScheduleListAscending;

      queryFn(options)
        .then(({ data }) => {
          if (isGetPostScheduleListQuery(data)) {
            const newPosts = data.workspace.queue.cursorPagination?.items || [];

            const returnCount = newPosts.length;
            const isLimit = returnCount < numberOfPostsToFetch;

            if (scrollPosition === 'TOP') {
              setCorePostItems(existing => [...newPosts, ...existing]);
              setLimitsReached(old => ({
                lower: old.lower,
                upper: isLimit
              }));
            } else {
              setCorePostItems(existing => [...existing, ...newPosts]);
              setLimitsReached(old => ({
                upper: old.upper,
                lower: isLimit
              }));
            }
          }
        })
        .finally(() => {
          setLoadingInitial(false);
        });
    },
    [getPostScheduleListAscending, getPostScheduleListDescending, setLoadingInitial, sortOrder]
  );

  // Remove duplicates in corePostItems, mainly for the first/last situation
  const filteredCorePostItems: PostItemFragment[] = useMemo(() => {
    const postMap = new Map<string, PostItemFragment>();
    corePostItems.forEach(p => postMap.set(p.id, p));
    return Array.from(postMap.values());
  }, [corePostItems]);

  type LibraryFetchDetails = {
    fetchMoreFunction: typeof postAscendingResultsFetchMore | typeof postDescendingResultsFetchMore;
    adjustedSortOrder: QueueSortOrder;
    fetchVariables: typeof postListAscendingResultsVariables | typeof postListDescendingResultsVariables;
    posts: PostItemFragment[];
  };

  const determineLibraryFetchDetails = useCallback(
    (sortOrder: QueueSortOrder, scrollPosition: 'TOP' | 'BOTTOM'): LibraryFetchDetails => {
      const posts: PostItemFragment[] = filteredCorePostItems;

      if (scrollPosition === 'BOTTOM') {
        if (sortOrder === QueueSortOrder.ASCENDING) {
          return {
            fetchMoreFunction: postAscendingResultsFetchMore,
            adjustedSortOrder: QueueSortOrder.ASCENDING,
            fetchVariables: postListAscendingResultsVariables,
            posts
          };
        } else {
          return {
            fetchMoreFunction: postDescendingResultsFetchMore,
            adjustedSortOrder: QueueSortOrder.DESCENDING,
            fetchVariables: postListDescendingResultsVariables,
            posts
          };
        }
      } else {
        if (sortOrder === QueueSortOrder.ASCENDING) {
          return {
            fetchMoreFunction: postDescendingResultsFetchMore,
            adjustedSortOrder: QueueSortOrder.DESCENDING,
            fetchVariables: postListDescendingResultsVariables,
            posts
          };
        } else {
          return {
            fetchMoreFunction: postAscendingResultsFetchMore,
            adjustedSortOrder: QueueSortOrder.ASCENDING,
            fetchVariables: postListAscendingResultsVariables,
            posts
          };
        }
      }
    },
    [
      filteredCorePostItems,
      postAscendingResultsFetchMore,
      postDescendingResultsFetchMore,
      postListAscendingResultsVariables,
      postListDescendingResultsVariables
    ]
  );

  const determineCursor = useCallback(
    (posts: PostItemFragment[], scrollPosition: 'TOP' | 'BOTTOM'): string | undefined => {
      const firstPost = posts[0];
      const lastPost = posts[posts.length - 1];

      if (firstPost && scrollPosition === 'TOP') {
        return PostScheduleHelpers.getCursorFromPostId(firstPost.id);
      } else if (lastPost && scrollPosition === 'BOTTOM') {
        return PostScheduleHelpers.getCursorFromPostId(lastPost.id);
      }
      return undefined;
    },
    []
  );

  const fetchMoreLibraryItems = useCallback(
    (scrollPosition: 'TOP' | 'BOTTOM') => {
      if (!selectedWorkspaceId) return;
      // Determine the fetch direction and details
      const fetchOrder = PostScheduleHelpers.determineLibraryScrollDirection(scrollPosition, sortType, LibrarySort);

      const { fetchMoreFunction, adjustedSortOrder, fetchVariables, posts } = determineLibraryFetchDetails(
        sortOrder,
        scrollPosition
      );

      if (!fetchVariables) return;

      const cursor = determineCursor(posts, scrollPosition);

      if (!cursor) {
        handleLimitCalculation(scrollPosition, true);
        return;
      }

      setLoadingMore(true);
      // Fetch the next set of posts
      fetchMoreFunction({
        variables: {
          ...fetchVariables,
          input: {
            ...fetchVariables.input,
            filters: fetchVariables.input.filters,
            pagination: {
              cursorPagination: {
                ...fetchVariables.input.pagination.cursorPagination,
                cursor,
                sortOrder: adjustedSortOrder,
                sortType
              }
            }
          }
        }
      })
        .then(results => {
          const { data, error } = results;

          if (error) {
            throw error;
          }

          const newPosts = data?.workspace.queue.cursorPagination?.items || [];
          PostScheduleHelpers.updatePostsAndLimits({
            posts: newPosts,
            scrollPosition,
            fetchOrder,
            numberOfPostsToFetch,
            setCorePostItems,
            setLimitsReached
          });
        })
        .catch((error: ApolloError) => {
          showToast({
            severity: 'error',
            summary: 'Retrieving posts',
            detail: remapApolloErrors(error)
          });
        })
        .finally(() => {
          setLoadingMore(() => false);
        });
    },
    [selectedWorkspaceId, sortType, determineLibraryFetchDetails, sortOrder, determineCursor, handleLimitCalculation]
  );

  const fetchInitialPostItems = useCallback(() => {
    setLimitsReached({ upper: true, lower: false });

    if (selectedWorkspaceId) {
      setCorePostItems([]);
      clearPostCache(selectedWorkspaceId);
    }

    const options = {
      variables: {
        workspaceId: selectedWorkspaceId as string,
        input: {
          ...defaultQueueInput,
          filters: {
            ...queueFiltersInput
          },
          ...{
            pagination: {
              cursorPagination: {
                count: numberOfPostsToFetch,
                sortType: sortType,
                sortOrder: sortOrder
              }
            },
            context: QueueContext.LIBRARY
          }
        }
      }
    };

    Promise.all([
      getPostScheduleLibraryItems(options, 'BOTTOM'),
      getPostLibraryQueueCounts(queueFiltersInput)
    ]).finally();
  }, [
    getPostLibraryQueueCounts,
    getPostScheduleLibraryItems,
    queueFiltersInput,
    selectedWorkspaceId,
    sortOrder,
    sortType
  ]);

  const timeslots = useMemo(() => {
    return timeslotsResults.data?.workspace.timeslots || [];
  }, [timeslotsResults.data?.workspace.timeslots]);

  useEffect(() => {
    fetchInitialPostItems();
  }, [fetchInitialPostItems, queueFiltersInput]);

  useEffect(() => {
    if (triggerDeleteReFetch) {
      fetchInitialPostItems();
      setTriggerDeleteReFetch(false);
    }
  }, [triggerDeleteReFetch, fetchInitialPostItems, setTriggerDeleteReFetch]);

  useEffect(() => {
    dispatch({
      type: 'setNewPostCloseCallback',
      payload: status => {
        if (status) {
          fetchInitialPostItems();
        }
      }
    });

    return () => {
      dispatch({
        type: 'setNewPostCloseCallback',
        payload: undefined
      });
    };
  }, [fetchInitialPostItems, dispatch]);

  useEffect(() => {
    if (triggerPostReFetch) {
      fetchInitialPostItems();
      setTriggerPostReFetch('');
    }
  }, [
    triggerPostReFetch,
    fetchInitialPostItems,
    getPostScheduleListAscending,
    queueFiltersInput,
    setTriggerPostReFetch
  ]);

  const controllerProps: PostScheduleViewProps = {
    ...props,
    ...{
      loadingInitial,
      loadingMore
    }
  };

  return (
    <PostScheduleLibraryController
      {...controllerProps}
      posts={filteredCorePostItems}
      queueCounts={postQueueCountResults.data?.workspace.queueCounts || queueCounts}
      setSortOrder={setSortOrder}
      setSortType={setSortType}
      limitsReached={limitsReached}
      fetchMorePostItems={fetchMoreLibraryItems}
      timeslots={timeslots}
    />
  );
};
